package org.example.project.firebase

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.withTimeoutOrNull

object AuthManager {

    private val auth = Firebase.auth

    val authStateChanged get() = auth.authStateChanged

    suspend fun register(
        email: String,
        password: String,
        username: String
    ): Boolean {
        return try {
            auth.createUserWithEmailAndPassword(email, password)
            waitForUserId() != null
        } catch (e: Exception) {
            println("Register Error: ${e.message}")
            false
        }
    }

    suspend fun login(
        email: String,
        password: String
    ): Boolean {
        return try {
            println("Attempting login for: $email")
            val result = auth.signInWithEmailAndPassword(email, password)
            println("Login result: ${result.user?.uid}")
            val uid = waitForUserId()
            println("Waited for UID: $uid")
            uid != null
        } catch (e: Exception) {
            println("Login Error: ${e.message}")
            e.printStackTrace()
            false
        }
    }

    suspend fun loginWithGitHub(): GitHubUserInfo? {
        return GitHubAuth.login()
    }

    suspend fun logout() {
        auth.signOut()
    }

    fun currentUserId(): String? = auth.currentUser?.uid

    fun currentUserEmail(): String? = auth.currentUser?.email

    /** Force refresh auth token before Firestore operations */
    suspend fun refreshAuthToken(): Boolean {
        val user = auth.currentUser
        if (user == null) {
            println("ERROR: No current user to refresh token")
            return false
        }
        return try {
            println("Refreshing auth token for user: ${user.uid}")
            user.getIdToken(true)
            println("Token refresh successful")
            true
        } catch (e: Exception) {
            println("ERROR refreshing token: ${e.message}")
            e.printStackTrace()
            false
        }
    }

    /** Check if user is currently authenticated */
    fun isAuthenticated(): Boolean {
        return auth.currentUser != null
    }

    /** Ensure user is authenticated before Firestore operations */
    suspend fun ensureAuthenticated(): Boolean {
        val uid = currentUserId()
        if (uid != null && uid.isNotEmpty()) {
            return true
        }

        return withTimeoutOrNull(10_000) {
            auth.authStateChanged.first { it != null }
            true
        } ?: false
    }

    /** Firestore requires an auth token; on web it may not be ready immediately after login. */
    suspend fun waitForUserId(timeoutMs: Long = 10_000): String? {
        currentUserId()?.let { return it }

        return withTimeoutOrNull(timeoutMs) {
            auth.authStateChanged.first { it != null }?.uid
        } ?: currentUserId()
    }

    /** Ensure auth token is ready for Firestore operations */
    suspend fun waitForAuthToken(timeoutMs: Long = 10_000): Boolean? {
        val uid = waitForUserId(timeoutMs) ?: return null
        if (uid.isEmpty()) return null

        return try {
            auth.currentUser?.getIdToken(true)
            true
        } catch (e: Exception) {
            println("Token refresh error: ${e.message}")
            false
        }
    }


}
